import React, { useState } from 'react';
import { Col, Form, Input, Spinner, Table } from 'reactstrap';
import { useForm, Controller } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Icon from '@mdi/react';
import { mdiDownload, mdiTrashCanOutline } from '@mdi/js';
import { toast } from 'react-toastify';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  fetchCodeList,
  downloadBlobType,
} from '../../../store/apis/commonService';
import {
  deleteTravelDocument,
  fetchTravelDocumentList,
  uploadTravelDocument,
} from '../../../store/apis/travelService';
import { DisplayModal, FileUploadDropzone } from '../../Common';
import { documentInitialValues, validationSchema } from './constants';
import { FileUtil } from '../../../utils';
import { getAuthUser } from '../../../utils/auth.util';
import eyeIcon from '../../../assets/icons/eyeicon.png';
import './UploadAttachment.scss';

function UploadAttachment({ id, ref }) {
  const queryClient = useQueryClient();
  const [fileInputKey, setFileInputKey] = useState(0);
  const [isDisplayModalOpen, setDisplayModalOpen] = useState(false);
  const [displayModalFile, setDisplayModalFile] = useState(null);

  const {
    control,
    watch,
    reset,
    formState: { errors },
    setValue,
    setError,
    clearErrors,
    trigger,
  } = useForm({
    defaultValues: documentInitialValues,
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
  });

  const newAttachment = watch();

  const { data: documentTypeOptions } = useQuery({
    queryKey: ['documentTypeOptions'],
    queryFn: () =>
      fetchCodeList({ type: 'DOC_TYPE', param1: 'DOC_TYPE_TRAVEL' }),
  });

  const { data: documentList = [], isLoading: isFetchingDocs } = useQuery({
    queryKey: ['documentList', id],
    queryFn: () =>
      fetchTravelDocumentList({ refNo: id, type: 'DOC_TYPE_TRAVEL' }),
  });

  const { mutate: uploadDocument, isLoading: isUploading } = useMutation({
    mutationFn: payload => uploadTravelDocument(payload),
    onSuccess: res => {
      if (res.flag === 'False') {
        toast.error(res.message, {
          className: 'toast-error',
        });
      } else {
        toast.success('Attachment uploaded successfully', {
          className: 'toast-success',
        });
        queryClient.invalidateQueries(['documentList', id]);
        reset();
        setFileInputKey(prev => prev + 1);
      }
    },
  });

  const { mutate: deleteDocument } = useMutation({
    mutationFn: payload => deleteTravelDocument(payload),
    onSuccess: res => {
      if (res.flag === 'False') {
        toast.error(res.message, {
          className: 'toast-error',
        });
      } else {
        toast.success('Attachment deleted successfully', {
          className: 'toast-success',
        });
        queryClient.invalidateQueries(['documentList', id]);
      }
    },
  });

  const onAddAttachment = () => {
    const { file: files, documentType, comments } = newAttachment;
    if (!files?.length || !documentType) {
      toast.error('Please select at least one file and a document type.', {
        className: 'toast-error',
      });
      return;
    }

    files.forEach(file => {
      const formData = new FormData();

      formData.append('type', 'DOC_TYPE_TRAVEL');
      formData.append('refNo', id);
      formData.append('docType', documentType);
      formData.append('comments', comments || '');
      formData.append('file', file);

      uploadDocument(formData);
    });
  };

  const downloadFileMutation = useMutation({
    mutationFn: payload => downloadBlobType(payload),
  });

  const handleDownload = async (fileName, type, docIndex) => {
    try {
      let result = null;
      if (docIndex === '-1') {
        result = await downloadFileMutation.mutateAsync({
          type: 'Passport_Travel_Request',
          hrCoCd: getAuthUser()?.hrCoCd,
          fileName,
        });
      } else {
        result = await downloadFileMutation.mutateAsync({
          refNo: id,
          type,
          fileName,
        });
      }

      FileUtil.downloadBlobFile(result, fileName || 'attachment');
    } catch (error) {
      toast.error('Failed to download file.', {
        className: 'toast-error',
      });
    }
  };

  const handleFileDisplay = async (fileName, type, docIndex) => {
    try {
      let result = null;

      if (docIndex === -1) {
        result = await downloadFileMutation.mutateAsync({
          type: 'Passport_Travel_Request',
          hrCoCd: getAuthUser()?.hrCoCd,
          fileName,
        });
      } else {
        result = await downloadFileMutation.mutateAsync({
          refNo: id,
          type,
          fileName,
        });
      }

      // Create a Blob URL
      const blob = new Blob([result], {
        type: result.type || 'application/pdf',
      });

      setDisplayModalFile(
        new File([blob], fileName, {
          type: blob.type,
        }),
      );

      // Store in state so the modal can use it
      setDisplayModalOpen(true);
    } catch (error) {
      toast.error('Failed to display file.', {
        className: 'toast-error',
      });
    }
  };

  const onDeleteAttachment = docId => {
    if (docId) {
      deleteDocument({ id: docId, type: 'Travel_Request' });
    }
  };

  return (
    <div
      ref={ref}
      className="d-flex flex-column gap-2 ba-form attachment-invalid"
    >
      <div className="d-flex flex-row align-items-center justify-content-between">
        <span className="fw-semibold">Upload Attachment</span>
        <button
          type="button"
          className="custom-secondary-btn"
          onClick={onAddAttachment}
          disabled={isUploading}
        >
          <span>{isUploading ? 'Uploading...' : 'Add Document'}</span>
        </button>
      </div>

      <Form className="d-flex flex-column gap-2">
        <div className="d-flex flex-row align-items-start gap-2 mb-2">
          <Col className="external-filter-col">
            <span className="ba-form-label">
              Browse File<span className="text-danger">*</span>
            </span>
            <Controller
              key={fileInputKey}
              control={control}
              name="file"
              render={({ field }) => (
                <FileUploadDropzone
                  multiple="true"
                  error={errors.file}
                  onDrop={async acceptedFiles => {
                    try {
                      await validationSchema.fields.file.validate(
                        acceptedFiles,
                      );
                      clearErrors('files');
                      setValue('file', acceptedFiles, {
                        shouldValidate: true,
                      });
                    } catch (err) {
                      setError('file', {
                        type: 'manual',
                        message: err.message,
                      });
                    }
                  }}
                  tooltipTitle="Upload any mandatory documents for the leave you are requesting for (as proof)."
                  selectedFiles={field.value}
                  className={errors.file ? 'is-invalid' : ''}
                  handleDeleteFile={file => {
                    field.onChange(
                      field.value.filter(f => f.name !== file.name),
                    );
                    trigger();
                  }}
                />
              )}
            />
            {errors.file && (
              <div className="text-danger">{errors.file.message}</div>
            )}
          </Col>

          <Col className="external-filter-col">
            <span className="ba-form-label">
              Document Type<span className="text-danger">*</span>
            </span>
            <Controller
              control={control}
              name="documentType"
              render={({ field }) => (
                <Input
                  type="select"
                  className="external-filter-input"
                  {...field}
                >
                  <option value="">Select Document Type</option>
                  {documentTypeOptions?.map(option => (
                    <option key={option.Code} value={option.Code}>
                      {option.Description}
                    </option>
                  ))}
                </Input>
              )}
            />
            {errors.documentType && (
              <div className="text-danger">{errors.documentType.message}</div>
            )}
          </Col>
          <Col className="external-filter-col">
            <span className="ba-form-label">Comments</span>
            <Controller
              control={control}
              name="comments"
              render={({ field }) => (
                <Input
                  type="text"
                  className="external-filter-input"
                  placeholder="Enter Comments"
                  {...field}
                />
              )}
            />
          </Col>
        </div>
      </Form>

      <div className="table-responsive">
        <Table className="ba-table" id="ba-doc-table">
          <thead>
            <tr>
              <th>Document Type</th>
              <th>File Name</th>
              <th>Uploaded By</th>
              <th>Uploaded Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {documentList.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center">
                  No uploaded documents.
                </td>
              </tr>
            )}
            {isFetchingDocs && (
              <tr>
                <td colSpan={5} className="text-center">
                  <Spinner color="primary" />
                </td>
              </tr>
            )}
            {documentList.map(doc => (
              <tr key={doc.ID}>
                <td>{doc.DocType}</td>
                <td>{doc.DocName}</td>
                <td>{doc.UploadedBy}</td>
                <td>{doc.UploadedDate}</td>
                <td>
                  <div className="d-flex flex-row gap-2">
                    <button
                      type="button"
                      className="btn btn-link p-0"
                      title="View Document"
                      onClick={() =>
                        handleFileDisplay(
                          doc.DocName,
                          'Travel_Request',
                          doc.DocIndex,
                        )
                      }
                    >
                      <img
                        src={eyeIcon}
                        alt="View"
                        style={{ height: '15px' }}
                      />
                    </button>
                    <button
                      type="button"
                      className="btn btn-link p-0"
                      title="Download Document"
                      onClick={() =>
                        handleDownload(
                          doc.DocName,
                          'Travel_Request',
                          doc.DocIndex,
                        )
                      }
                    >
                      <Icon path={mdiDownload} size={0.8} color="#017EBD" />
                    </button>
                    <button
                      type="button"
                      className="btn btn-link p-0"
                      onClick={() => onDeleteAttachment(doc.ID)}
                      title="Delete Document"
                    >
                      <Icon
                        path={mdiTrashCanOutline}
                        size={0.8}
                        color="#e71d36"
                      />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        <DisplayModal
          isModalOpen={isDisplayModalOpen}
          toggle={() => setDisplayModalOpen(false)}
          title="View Attachments"
          file={displayModalFile}
        />
      </div>
    </div>
  );
}

export default UploadAttachment;
